import React, { Fragment, useEffect, useState } from 'react'
import { Col, Row, Table, CardBody } from 'reactstrap'
import { Link } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { API_URL } from './utils/Constants/URLS'
import PageHeader from './utils/PageHeader'
import PageLoader from './utils/PageLoader'
import '../css/OrgContacts.css'
import '../css/common.css'
import { useSelector } from 'react-redux'

const ViewSoInvoice = () => {
  const token = localStorage.getItem('token')
  const [soData, setSoData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchSo, setSearchSo] = useState('')
  const [debouncedSearch] = useDebounce(searchSo, 1500)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [nextPage, setNextPage] = useState(null)
  const [prevPage, setPrevPage] = useState(null)
  const [pageLoader, setPageLoader] = useState(false)
  const userDetails = useSelector(store => store.user)

  // Reset to page 1 whenever debounced search changes
  useEffect(() => {
    setPageNumber(1)
  }, [debouncedSearch])

  useEffect(() => {
    const fetchSO = async () => {
      setPageLoader(true)
      try {
        let qs = `?limit=100&page=${pageNumber}${userDetails?.role === 'executive' ? `&businessLine=${userDetails?.businessLine}&leadCreatedBy=${userDetails?.id}` : userDetails?.role === 'manager' ? `&businessLine=${userDetails?.businessLine}` : ''}`
        if (debouncedSearch) qs += `&search=${debouncedSearch}`

        const res = await fetch(`${API_URL}leads/sales-orders${qs}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        const docs = result?.data?.docs ?? result?.data ?? []
        setSoData(docs)
        setTotalPages(result?.data?.totalPages ?? 1)
        setNextPage(result?.data?.nextPage ?? null)
        setPrevPage(result?.data?.prevPage ?? null)
      } catch (e) {
        console.error('fetchSO error', e)
      } finally {
        setPageLoader(false)
      }
    }

    fetchSO()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pageNumber, debouncedSearch])

  const formatDate = iso => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return iso
    }
  }

  // pick best place to read items from so:
  // prefer so.lineItems when it has model/listPrice/rate/value,
  // otherwise try so.lead.productRequirements.crossSells
  const getDisplayLineItems = (so) => {
    const soLineItems = Array.isArray(so?.lineItems) ? so.lineItems : []
    const hasModelInfo = soLineItems.length > 0 && (
      soLineItems[0].model ||
      soLineItems[0].listPrice ||
      soLineItems[0].rate ||
      soLineItems[0].value
    )
    if (hasModelInfo) return soLineItems

    const crossSells = so?.lead?.productRequirements?.crossSells
    if (Array.isArray(crossSells) && crossSells.length > 0) {
      return crossSells
    }

    return soLineItems
  }

  // render models handling multiple shapes:
  // - rich lineItem with model object
  // - crossSell object that contains model or modelName
  // - fallback string (id) or minimal item
  const renderModels = (lineItems) => {
    if (!Array.isArray(lineItems) || lineItems.length === 0) return '-'

    return lineItems.map((li, i) => {
      if (typeof li === 'string' || typeof li === 'number') {
        return (
          <div key={`${li}-${i}`}>
            {li} <small className='text-muted'>(qty: -)</small>
          </div>
        )
      }

      const modelObj = li?.model
      const name = modelObj?.modelName ?? li?.modelName ?? modelObj?._id ?? li?.partCode ?? '-'
      const qty = li?.quantity ?? li?.qty ?? '-'

      return (
        <div key={li?._id ?? `${name}-${i}`}>
          {name} <small className='text-muted'>({qty})</small>
        </div>
      )
    })
  }

  // compute total value:
  // 1) prefer summing values from displayLineItems if present (li.value)
  // 2) otherwise fallback to paymentTerms total
  const computeTotalValue = (so) => {
    const displayLineItems = getDisplayLineItems(so)
    if (Array.isArray(displayLineItems) && displayLineItems.length > 0) {
      const sum = displayLineItems.reduce((acc, li) => {
        const val = Number(li?.rate) * Number(li?.quantity)
        return acc + (isFinite(val) ? val : 0)
      }, 0)
      if (sum > 0) return sum
    }

    if (Array.isArray(so?.paymentTerms) && so.paymentTerms.length) {
      return so.paymentTerms.reduce((acc, p) => acc + (Number(p?.amount) || 0), 0)
    }

    return 0
  }

  // Build a compact pagination list: 1 2 3 ... n
  // - If totalPages is small (<=4), show all page numbers in order.
  // - Otherwise always show the first 3 pages, an ellipsis, then the last page.
  const getPaginationItems = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    return [1, 2, 3, '...', totalPages]
  }

  return (
    <Fragment>
      <PageHeader
        pageName='START INVOICE'
        breadcrump1='Home'
        breadcrump2='Start Invoice'
        link='/dashboard'
      />

      <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3 '>
        {/* Search + Action row */}
        <CardBody className='bg-white p-3 mb-3'>
          <form>
            <Row>
              <Col lg={3} md={4} sm={6} xs={12}>
                <input
                  className='form-control'
                  type='text'
                  placeholder='Search Sales Orders'
                  value={searchSo}
                  onChange={e => setSearchSo(e.target.value)}
                />
              </Col>
            </Row>
          </form>
        </CardBody>

        {/* Table */}
        <CardBody>
          <div className='table-responsive w-full mb-3'>
            {pageLoader && <PageLoader />}

            {!pageLoader && (
              <Table hover className='align-middle table-nowrap'>
                <thead className='table-light'>
                  <tr>
                    <th>#</th>
                    <th>Sales Order No</th>
                    <th>Client</th>
                    <th>Models</th>
                    <th>Value</th>
                    <th>Date of SO</th>
                  </tr>
                </thead>

                <tbody>
                  {soData.length === 0 ? (
                    <tr>
                      <td colSpan='9' className='text-center py-4'>
                        No sales orders found
                      </td>
                    </tr>
                  ) : (
                    soData.map((so, idx) => {
                      const displayLineItems = getDisplayLineItems(so)
                      const totalValue = computeTotalValue(so)
                      return (
                        <tr key={so._id ?? idx}>
                          <td>{(pageNumber - 1) * 10 + idx + 1}</td>
                          <td>
                            <Link
                              to={`${so?._id}/${so?.salesOrderNumber}`}
                              className='text-dark no-underline'
                            >
                              {so.salesOrderNumber ?? '-'}
                            </Link>
                          </td>
                          <td>
                            {so?.lead?.client?.companyName ?? so?.client ?? '-'}
                          </td>
                          <td>{renderModels(displayLineItems)}</td>
                          <td>
                            {totalValue
                              ? totalValue.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })
                              : '-'}
                          </td>
                          <td>{formatDate(so.dateOfSO ?? so.createdAt)}</td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </Table>
            )}
          </div>

          {/* Pagination: Prev 1 2 3 ... n Next */}
          <div className='d-flex justify-content-end'>
            <nav aria-label='...'>
              <ul className='pagination'>
                <li className={`page-item ${!prevPage ? 'disabled' : ''}`}>
                  <button
                    className='page-link text-primary'
                    onClick={() => prevPage && setPageNumber(prevPage)}
                  >
                    Previous
                  </button>
                </li>

                {getPaginationItems().map((item, i) =>
                  item === '...' ? (
                    <li key={`dots-${i}`} className='page-item disabled'>
                      <span className='page-link'>...</span>
                    </li>
                  ) : (
                    <li
                      key={item}
                      className={`page-item ${pageNumber === item ? 'active' : ''}`}
                    >
                      <button
                        className={`page-link ${pageNumber === item ? 'text-white' : 'text-primary'}  `}
                        onClick={() => setPageNumber(item)}
                      >
                        {item}
                      </button>
                    </li>
                  )
                )}

                <li className={`page-item ${!nextPage ? 'disabled' : ''}`}>
                  <button
                    className='page-link text-primary'
                    onClick={() => nextPage && setPageNumber(nextPage)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </CardBody>
      </div>
    </Fragment>
  )
}

export default ViewSoInvoice